/*
 * @Description: 实时定位驾驶舱 — 右侧「团队成员」卡片
 *   按在线/离线分组展示；行点击与地图气泡双向联动；「轨迹 >」入口。
 */
import React from 'react'
import { Select } from 'antd'
import { DownOutlined, RightOutlined, UpOutlined } from '@ant-design/icons'
import { RealtimeMemberItem } from '../../types/cockpit.types'
import { MEMBER_BIND_FILTER_OPTIONS, MemberBindFilter } from '../../constants'

const { Option } = Select

interface TeamMembersCardProps {
  /** A2 成员列表（可能已按状态筛选） */
  members: RealtimeMemberItem[]
  /** 团队成员总数（标题展示，不随筛选变化） */
  total: number
  /** 当前选中成员 userId */
  selectedUserID: string | null
  /** 是否收起 */
  collapsed: boolean
  /** 绑定筛选当前值 */
  bindFilter: MemberBindFilter
  /** 绑定筛选变化 */
  onChangeBindFilter: (v: MemberBindFilter) => void
  /** 成员筛选已选人数（0 = 未筛选） */
  memberFilterCount: number
  /** 点击「筛成员」打开成员选择弹窗 */
  onClickMemberFilter: () => void
  /** 是否还有下一页 */
  hasMore: boolean
  /** 上拉触底加载下一页 */
  onLoadMore: () => void
  /** 点击行（选中成员，联动地图） */
  onSelectMember: (userId: string) => void
  /** 点击「轨迹 >」 */
  onClickTrack: (userId: string) => void
  /** 切换收起/展开 */
  onToggleCollapse: () => void
}

/** 滚动到距底部多少像素时触发加载下一页 */
const LOAD_MORE_THRESHOLD_PX = 60

/** 单成员行 */
function MemberRow({
  member,
  selected,
  onSelectMember,
  onClickTrack
}: {
  member: RealtimeMemberItem
  selected: boolean
  onSelectMember: (userId: string) => void
  onClickTrack: (userId: string) => void
}) {
  const firstChar = (member.name || '?').slice(0, 1)
  return (
    <div
      className={`cockpit-member-row ${selected ? 'is-selected' : ''}`}
      onClick={() => onSelectMember(member.userId)}
    >
      <div className="cockpit-member-row__avatar">
        {member.avatar ? (
          <img src={member.avatar} alt="" />
        ) : (
          <span className="cockpit-member-row__initial">{firstChar}</span>
        )}
        <span
          className={`cockpit-member-row__dot ${
            member.online ? 'is-online' : 'is-offline'
          }`}
        />
      </div>
      <div className="cockpit-member-row__info">
        <div className="cockpit-member-row__name">{member.name}</div>
        <div className="cockpit-member-row__addr">{member.address}</div>
      </div>
      <span
        className="cockpit-member-row__track"
        onClick={(e) => {
          e.stopPropagation()
          onClickTrack(member.userId)
        }}
      >
        轨迹 <RightOutlined />
      </span>
    </div>
  )
}

const TeamMembersCard: React.FC<TeamMembersCardProps> = ({
  members,
  total,
  selectedUserID,
  collapsed,
  bindFilter,
  onChangeBindFilter,
  memberFilterCount,
  onClickMemberFilter,
  hasMore,
  onLoadMore,
  onSelectMember,
  onClickTrack,
  onToggleCollapse
}) => {
  // 滚动接近底部时加载下一页
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget
    if (
      hasMore &&
      el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX
    ) {
      onLoadMore()
    }
  }

  return (
    <div className={`cockpit-card ${collapsed ? 'is-collapsed' : ''}`}>
      <div className="cockpit-card__header" onClick={onToggleCollapse}>
        <span className="cockpit-card__title">团队成员 ({total})</span>
        <span className="cockpit-card__toggle">
          {collapsed ? '展开' : '收起'}
          {collapsed ? <DownOutlined /> : <UpOutlined />}
        </span>
      </div>

      {!collapsed && (
        <div className="cockpit-card__body" onScroll={handleScroll}>
          {/* 筛选：绑定筛选下拉 + 筛成员（打开成员选择弹窗） */}
          <div className="cockpit-member-filters">
            <Select
              className="cockpit-member-filters__bind"
              value={bindFilter}
              onChange={onChangeBindFilter}
              dropdownMatchSelectWidth={false}
            >
              {MEMBER_BIND_FILTER_OPTIONS.map((o) => (
                <Option key={o.value} value={o.value}>
                  {o.label}
                </Option>
              ))}
            </Select>
            <div
              className="cockpit-member-filters__pick"
              onClick={onClickMemberFilter}
            >
              <span
                className={
                  memberFilterCount
                    ? 'cockpit-member-filters__pick-text'
                    : 'cockpit-member-filters__pick-text is-placeholder'
                }
              >
                {memberFilterCount ? `已选 ${memberFilterCount} 人` : '筛成员'}
              </span>
              <DownOutlined />
            </div>
          </div>

          {members.map((member) => (
            <MemberRow
              key={member.userId}
              member={member}
              selected={selectedUserID === member.userId}
              onSelectMember={onSelectMember}
              onClickTrack={onClickTrack}
            />
          ))}

          {members.length === 0 && (
            <div className="cockpit-card__empty">暂无成员</div>
          )}

          {hasMore && <div className="cockpit-card__loadmore">加载中…</div>}
        </div>
      )}
    </div>
  )
}

export default TeamMembersCard
